import jwtDecode from 'jwt-decode';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { logoutRoute } from 'App/common/routeLookup';
import AuthService from '../../../services/AuthService';
import SessionStorageService from '../../../services/SessionStorageService';
import TokenManagerService from '../../../services/TokenManagerService';
import notify from '../../../utils/osLevelNotification';

const TIMER_START_KEY = 'sessionTimerStart';
const TIMEOUT_LENGTH_KEY = 'sessionTimeoutLength';
const ACTIVITY_THROTTLE_MS = 60_000;

function SessionTimer() {
    const navigate = useNavigate();
    const location = useLocation();
    const [remainingSeconds, setRemainingSeconds] = useState(30 * 60);
    const [showNotice, setShowNotice] = useState(false);

    const intervalRef = useRef(null);
    const loggingOutRef = useRef(false);
    const timerStartRef = useRef(null);
    const timeoutLengthSecondsRef = useRef(30 * 60);
    const explicitResetInProgressRef = useRef(false);
    const notificationRef = useRef(null);
    const resetTimerRef = useRef(null);

    const clearTimer = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const dismissNotification = () => {
        if (notificationRef.current) {
            notificationRef.current.close();
            notificationRef.current = null;
        }
    };

    const handleNotificationClick = () => {
        window.focus();
        dismissNotification();
        if (resetTimerRef.current) resetTimerRef.current();
    };

    const startTimer = (sharedStartTime = null) => {
        clearTimer();

        if (!sharedStartTime) {
            const rawToken = SessionStorageService.getJwt();
            if (rawToken) {
                const decoded = jwtDecode(rawToken);
                const lengthMinutes = decoded.data.TimeoutLength || 30;
                timeoutLengthSecondsRef.current = lengthMinutes * 60;
                localStorage.setItem(TIMEOUT_LENGTH_KEY, String(lengthMinutes * 60));
            }
            timerStartRef.current = Date.now();
            localStorage.setItem(TIMER_START_KEY, String(timerStartRef.current));
        } else {
            timerStartRef.current = sharedStartTime;
        }

        intervalRef.current = setInterval(() => {
            if (loggingOutRef.current) {
                clearTimer();
                return;
            }

            if (!SessionStorageService.getJwt()) return;

            const elapsedSeconds = Math.floor((Date.now() - timerStartRef.current) / 1000);
            const secondsLeft = timeoutLengthSecondsRef.current - elapsedSeconds;

            setRemainingSeconds(secondsLeft);

            if (secondsLeft <= 0) {
                clearTimer();
                loggingOutRef.current = true;
                navigate(logoutRoute);
                return;
            }

            if (secondsLeft < 240) {
                if (!notificationRef.current) {
                    notificationRef.current = notify(
                        `Your session will expire in ${Math.floor(secondsLeft / 60)} minutes. Click to extend.`,
                        handleNotificationClick
                    );
                }
                setShowNotice(true);
            } else {
                dismissNotification();
                setShowNotice(false);
            }
        }, 330);
    };

    const resetTimer = () => {
        if (loggingOutRef.current) return;
        clearTimer();
        explicitResetInProgressRef.current = true;
        setShowNotice(false);
        dismissNotification();
        AuthService.updateJwt()
            .then(() => {
                if (!loggingOutRef.current) startTimer();
            })
            .catch((error) => {
                const status = error?.response?.status;
                const isDefinitive = status === 401 || !TokenManagerService.getAccessToken();
                if (isDefinitive && !loggingOutRef.current) {
                    loggingOutRef.current = true;
                    clearTimer();
                    navigate(logoutRoute);
                } else if (!isDefinitive && !loggingOutRef.current) {
                    startTimer();
                }
            })
            .finally(() => {
                explicitResetInProgressRef.current = false;
            });
    };

    resetTimerRef.current = resetTimer;

    /**
     * Cross-tab timer sync
     *
     * Listens for localStorage changes written by other browser tabs. When a
     * sibling tab resets its session (writing a new value to TIMER_START_KEY) this
     * tab adopts the same start time and timeout length so all tabs count down in
     * unison. Without this, each tab would run an independent countdown and could
     * log the user out at a different moment.
     */
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (loggingOutRef.current) return;
            if (event.key === TIMER_START_KEY && event.newValue) {
                const newStartTime = Number(event.newValue);
                if (newStartTime && newStartTime !== timerStartRef.current) {
                    const storedTimeout = localStorage.getItem(TIMEOUT_LENGTH_KEY);
                    if (storedTimeout) {
                        timeoutLengthSecondsRef.current = Number(storedTimeout);
                    }
                    setShowNotice(false);
                    startTimer(newStartTime);
                }
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Activity-based session refresh
     *
     * Registers capture-phase listeners for common user interactions
     * (mousedown, keydown, scroll, touchstart) as well as the Page Visibility
     * API so returning to the tab counts as activity. When the user is active,
     * resetTimer() is called — but at most once per ACTIVITY_THROTTLE_MS — which
     * refreshes the JWT and restarts the countdown. The capture phase is used so
     * the handler fires before any component-level handlers that might stop
     * propagation. Resets that are already in flight (explicitResetInProgressRef)
     * are skipped to avoid overlapping API calls.
     */
    useEffect(() => {
        let lastResetTime = Date.now();
        const handleUserActivity = () => {
            if (loggingOutRef.current || explicitResetInProgressRef.current) return;
            const now = Date.now();
            if (now - lastResetTime < ACTIVITY_THROTTLE_MS) return;
            lastResetTime = now;
            resetTimer();
        };
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
        events.forEach((evt) => window.addEventListener(evt, handleUserActivity, { passive: true, capture: true }));
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') handleUserActivity();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            events.forEach((evt) => window.removeEventListener(evt, handleUserActivity, { capture: true }));
            document.removeEventListener('visibilitychange', handleVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Per-route timer lifecycle
     *
     * Runs on mount and again whenever the URL pathname changes. It:
     *   1. Clears loggingOutRef so a navigation away from /logout re-enables the
     *      timer (guards in resetTimer and the activity handler both check this
     *      flag, so it must be cleared before calling resetTimer).
     *   2. Calls resetTimer() to refresh the JWT and start a fresh countdown for
     *      the new page — navigation is treated as intentional activity.
     *   3. Returns clearTimer() as a cleanup function so the interval is stopped
     *      when the component unmounts, preventing state updates on an unmounted
     *      component.
     *
     * This is also responsible for the very first timer start on mount; the
     * activity-based effect only registers listeners and never calls resetTimer
     * directly at setup time.
     */
    useEffect(() => {
        loggingOutRef.current = false;
        resetTimer();
        return () => clearTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Derive display values at render time instead of storing them in state
    const safeSeconds = Math.max(remainingSeconds, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = Math.ceil(safeSeconds % 60);
    const secondsDisplay = seconds < 10 ? `0${seconds}` : String(seconds);
    const timeoutLengthMinutes = Math.floor(timeoutLengthSecondsRef.current / 60);

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            style={{ cursor: 'pointer' }}
            onMouseDown={() => {
                explicitResetInProgressRef.current = true;
            }}
            onClick={resetTimer}
        >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a className="icon" style={{ display: 'flex', gap: 2 }}>
                <i className="fas fa-fw fa-clock" />
                <span>{`${minutes}:${secondsDisplay}`}</span>
            </a>
            {showNotice && (
                <div id="notice_div">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                    <a className="bg-warning" id="resetTimeLink" onClick={resetTimer}>
                        {`Your session is about to expire, please click here to reset session
                        duration back to ${timeoutLengthMinutes} minutes.`}
                    </a>
                </div>
            )}
        </div>
    );
}

export default SessionTimer;
